import React, { useState } from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import {
  ArrowRight, Play, Radio, Receipt, Calculator, ShieldCheck, Bell, Cloud, Shield,
  Wifi, PhoneCall, ShoppingBag, ClipboardList, Users, Handshake, Network, Zap,
  Activity, Database, CheckCircle, ChevronDown, TrendingUp, Globe
} from 'lucide-react';
import {
  telcosHeroData, telcosStatsData, convergentBillingData, digitalServicesData,
  bssOssFeaturesData, networkIntegrationData, telcoUseCasesData, telcosFAQData, telcoTestimonialData
} from '../../data/telcosData';

const iconMap = {
  Receipt, Calculator, ShieldCheck, Bell, Cloud, Shield, Wifi, PhoneCall,
  ShoppingBag, ClipboardList, Users, Handshake, Network, Zap, Activity, Database
};

// Hero Section
const TelcosHero = () => (
  <section className="relative min-h-screen overflow-hidden bg-dark-ellipse">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                         radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />
    </div>
    <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#8bcaf9]/30 rounded-full blur-3xl animate-pulse" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
        <div className="text-left">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
            <Radio className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">{telcosHeroData.tagline}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {telcosHeroData.title}
          </h1>
          <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl leading-relaxed">
            {telcosHeroData.subtitle}
          </p>
          <div className="flex flex-wrap gap-4">
            <Button size="lg" className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-8 py-6 rounded-full text-lg transition-all hover:scale-105 hover:shadow-xl group">
              {telcosHeroData.cta}
              <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg transition-all hover:scale-105 group bg-transparent">
              <Play className="mr-2 w-5 h-5" />
              {telcosHeroData.secondaryCta}
            </Button>
          </div>
        </div>

        <div className="relative hidden lg:block">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-1 shadow-2xl border border-white/20">
            <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    <Radio className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-semibold">Telco Platform</span>
                </div>
                <span className="text-green-400 text-sm">En vivo</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: 'Clientes Activos', value: '2.4M', change: '+8%' },
                  { label: 'Servicios Digitales', value: '120+', change: '+15' },
                  { label: 'Ingresos Digitales', value: '$4.2M', change: '+45%' },
                  { label: 'Churn Rate', value: '1.8%', change: '-0.5%' }
                ].map((stat) => (
                  <div key={stat.label} className="bg-white/5 rounded-xl p-4">
                    <p className="text-[#8bcaf9] text-xs mb-1">{stat.label}</p>
                    <p className="text-white font-bold text-lg">{stat.value}</p>
                    <span className="text-green-400 text-xs">{stat.change}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-white text-sm">Ingresos por Servicio</span>
                  <span className="text-green-400 text-xs">Este mes</span>
                </div>
                <div className="space-y-2">
                  {[
                    { name: 'Conectividad', value: 65 },
                    { name: 'Cloud Services', value: 25 },
                    { name: 'Seguridad', value: 10 }
                  ].map((item) => (
                    <div key={item.name} className="flex items-center gap-3">
                      <span className="text-white/60 text-xs w-20">{item.name}</span>
                      <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${item.value}%`, background: 'linear-gradient(to right, #1297fd, #40aaf8)' }} />
                      </div>
                      <span className="text-white text-xs w-8">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl transform rotate-3 hover:rotate-0 transition-transform">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-gray-900 font-bold text-sm">Nuevo Revenue</p>
                <p className="text-green-500 text-xs font-medium">+45% digital</p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl transform -rotate-3 hover:rotate-0 transition-transform">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#1297fd]" />
              <div>
                <p className="text-[#1297fd] font-bold text-lg">2M+</p>
                <p className="text-[#c3c3c5] text-xs">Facturas/mes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        {telcosStatsData.map((stat, idx) => (
          <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all hover:scale-105">
            <p className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</p>
            <p className="text-white/70 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="absolute bottom-0 left-0 right-0">
      <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
      </svg>
    </div>
  </section>
);

// Convergent Billing Section
const ConvergentBillingSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            Facturación Convergente
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Una factura,{' '}
            <span className="text-[#1297fd]">todos los servicios</span>
          </h2>
          <p className="text-lg text-[#c3c3c5] mb-8 leading-relaxed">
            Consolida conectividad, voz, cloud y servicios digitales en una única factura. Rating en tiempo real, revenue assurance y dunning automatizado.
          </p>
          <div className="space-y-6">
            {convergentBillingData.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={feature.id} className="flex gap-4 group">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#1297fd] transition-colors">{feature.title}</h4>
                    <p className="text-[#c3c3c5] text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white font-semibold">Billing Engine</span>
              <span className="text-green-400 text-sm">99.99% precisión</span>
            </div>
            <div className="bg-white/5 rounded-2xl p-6 mb-6">
              <p className="text-[#8bcaf9] text-xs mb-2">Factura Convergente - Cliente #48291</p>
              <div className="space-y-3">
                {[
                  { service: 'FTTH 600Mb', amount: '€49.90' },
                  { service: 'Móvil Ilimitado x2', amount: '€39.80' },
                  { service: 'Microsoft 365 Business', amount: '€12.50' },
                  { service: 'Backup Cloud 100GB', amount: '€5.90' }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-white/80 text-sm">{item.service}</span>
                    <span className="text-white font-medium">{item.amount}</span>
                  </div>
                ))}
                <div className="border-t border-white/10 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-[#40aaf8] font-bold text-xl">€108.10</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <p className="text-white font-bold text-xl">2.4M</p>
                <p className="text-[#8bcaf9] text-xs">Facturas/mes</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <p className="text-white font-bold text-xl">€0</p>
                <p className="text-[#8bcaf9] text-xs">Fugas detectadas</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Digital Services Section
const DigitalServicesSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Servicios Digitales
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Expande tu oferta con{' '}
          <span className="text-[#1297fd]">servicios de valor agregado</span>
        </h2>
        <p className="text-lg text-[#c3c3c5] max-w-2xl mx-auto">
          Más de 120 servicios digitales listos para integrar en tu catálogo y ofrecer a tus clientes.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {digitalServicesData.map((service) => {
          const IconComponent = iconMap[service.icon];
          return (
            <div key={service.id} className="group bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl hover:-translate-y-2">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
              </div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#1297fd] transition-colors">{service.title}</h3>
              <p className="text-[#c3c3c5] text-sm mb-4">{service.description}</p>
              <div className="flex flex-wrap gap-2">
                {service.examples.map((example) => (
                  <span key={example} className="text-xs px-2 py-1 bg-[#e3e4e6] rounded-lg text-gray-600">{example}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// BSS/OSS Section
const BssOssSection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4 border border-white/20">
          BSS/OSS
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          Gestión completa del negocio telco
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {bssOssFeaturesData.map((feature) => {
          const IconComponent = iconMap[feature.icon];
          return (
            <div key={feature.id} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-all">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
              </div>
              <h3 className="font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-white/70 text-sm">{feature.description}</p>
            </div>
          );
        })}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {networkIntegrationData.map((item) => {
          const IconComponent = iconMap[item.icon];
          return (
            <div key={item.id} className="flex gap-3 bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                {IconComponent && <IconComponent className="w-4 h-4 text-white" />}
              </div>
              <div>
                <h4 className="font-semibold text-white text-sm mb-1">{item.title}</h4>
                <p className="text-white/60 text-xs">{item.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Use Cases Section
const UseCasesSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Casos de Uso
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Estrategias{' '}
          <span className="text-[#1297fd]">probadas</span>
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {telcoUseCasesData.map((useCase, idx) => (
          <div key={useCase.id} className={`rounded-2xl p-6 ${idx === 0 ? 'bg-gradient-to-br from-[#1297fd] to-[#40aaf8] text-white' : 'bg-gradient-to-br from-gray-50 to-white border border-[#e3e4e6]'} hover:shadow-xl transition-all hover:-translate-y-1`}>
            <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${idx === 0 ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'}`}>
              {useCase.benefit}
            </div>
            <h3 className={`font-bold text-lg mb-2 ${idx === 0 ? 'text-white' : 'text-gray-900'}`}>{useCase.title}</h3>
            <p className={`text-sm ${idx === 0 ? 'text-white/80' : 'text-[#c3c3c5]'}`}>{useCase.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Testimonial Section
const TestimonialSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="bg-white rounded-3xl p-12 border border-[#e3e4e6] shadow-xl">
        <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
          <span className="text-white text-2xl font-bold">"</span>
        </div>
        <blockquote className="text-xl sm:text-2xl text-gray-900 mb-8 leading-relaxed">
          {telcoTestimonialData.quote}
        </blockquote>
        <div>
          {telcoTestimonialData.author && <p className="font-bold text-gray-900">{telcoTestimonialData.author}</p>}
          <p className="text-[#1297fd]">{telcoTestimonialData.role}, {telcoTestimonialData.company}</p>
          {telcoTestimonialData.location && <p className="text-[#c3c3c5] text-sm">{telcoTestimonialData.location}</p>}
        </div>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">FAQ</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Preguntas frecuentes</h2>
        </div>
        <div className="space-y-4">
          {telcosFAQData.map((item, idx) => (
            <div key={idx} className={`rounded-2xl border-2 transition-all overflow-hidden ${openIndex === idx ? 'border-[#8bcaf9] bg-white shadow-lg' : 'border-[#e3e4e6] bg-white'}`}>
              <button onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)} className="w-full p-6 text-left flex items-center justify-between gap-4">
                <span className={`font-semibold text-lg ${openIndex === idx ? 'text-[#1297fd]' : 'text-gray-900'}`}>{item.question}</span>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${openIndex === idx ? 'rotate-180' : 'bg-[#e3e4e6]'}`} style={openIndex === idx ? { background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' } : {}}>
                  <ChevronDown className={`w-5 h-5 ${openIndex === idx ? 'text-white' : 'text-gray-600'}`} />
                </div>
              </button>
              <div className={`overflow-hidden transition-all ${openIndex === idx ? 'max-h-96' : 'max-h-0'}`}>
                <div className="px-6 pb-6"><p className="text-[#c3c3c5] leading-relaxed">{item.answer}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Transforma tu negocio telco con servicios digitales</h2>
      <p className="text-lg text-white/80 mb-10">Diversifica ingresos, reduce churn y ofrece más valor a tus clientes con nuestra plataforma.</p>
      <div className="flex flex-wrap justify-center gap-4">
        <Button size="lg" className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-10 py-6 rounded-full transition-all hover:scale-105 group">
          Comenzar ahora
          <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
        </Button>
        <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 rounded-full transition-all hover:scale-105 bg-transparent">
          Solicitar demo
        </Button>
      </div>
    </div>
  </section>
);

// Main Page Component
const TelcosPage = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Telcos · BSS/OSS moderno, facturación convergente y servicios digitales"
        description="Plataforma BSS/OSS para operadores de telecomunicaciones: facturación convergente, integración de red, marketplace de servicios y automatización de alta velocidad."
        keywords="BSS OSS, telco, operador telecomunicaciones, billing convergente, 5G, network management"
        canonical="/telcos"
      />
      <Navbar />
      <TelcosHero />
      <ConvergentBillingSection />
      <DigitalServicesSection />
      <BssOssSection />
      <UseCasesSection />
      <TestimonialSection />
      <FAQSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default TelcosPage;
